<script setup lang="ts">
import { useUserManagementStore } from '@/stores/userManagement';
import { Table, UserAvatar } from '@/components/common';
import { TableColumnType } from '@/components/common/table/interfaces/TableColumnModel';
import { onMounted, ref } from 'vue';
import { UserModel } from '@/models/user';
import Chip from 'primevue/chip';
import { StudyUsersService } from '@/services';
import { cloneDeep } from 'lodash';
import UserExpandedNew from './UserExpandedNew.vue';
import type { StudyGroupModel } from '@/models';
import { useStudyGroupsStore } from '@/stores/studyGroups';
import StudyGroupDialog from './groups/StudyGroupDialog.vue';
import UserExpanded from './UserExpanded.vue';

const studyUsersStore = useUserManagementStore();
const groupStore = useStudyGroupsStore();

const expandedRows = ref<UserModel[]>([]);

const handleRowToggle = (clickedRow: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === clickedRow.identifier
  );

  if (index === -1) {
    expandedRows.value.push(clickedRow);
  } else {
    expandedRows.value.splice(index, 1);
  }
};

const onPerPageRowCountChanged = (perPage: number) => {
  expandedRows.value = [];
};

onMounted(async () => {
  await studyUsersStore.fetchCurrentUsers();
});

const getUserCombinedPermissions = (user: UserModel)  => {
  const userPermissions = cloneDeep(user.permissions);
  for (const group of user.groups ?? []) {
    if (group.permissions) {
      if (group.permissions.export) {
        userPermissions.export = true;
      }
      if (group.permissions.manageInvest) {
        userPermissions.manageInvest = true;
      }
      if (group.permissions.viewEntries) {
        userPermissions.viewEntries = true;
      }
      if (group.permissions.editCases) {
        userPermissions.editCases = true;
      }
      if (group.permissions.createStudy) {
        userPermissions.createStudy = true;
      }
    }
  }
  const permissionsList = StudyUsersService.parsePermissions(userPermissions);
  const userClone = cloneDeep(user);
  userClone.permissionsList = permissionsList;
  return userClone;
};

const openGroupDialog = ref<boolean>(false);

const groupEditClick = (data: StudyGroupModel) => {
  groupStore.updateGroup(data);
  openCloseGroupDialog();
};

const openCloseGroupDialog = (reloadGroups?: boolean) => {
  openGroupDialog.value = !openGroupDialog.value;
  if (reloadGroups === true) {
    groupStore.getStudyGroups();
  }
  studyUsersStore.currentUsers.forEach(user => {
    const groupIndex = user.groups?.findIndex(g => g.identifier === groupStore.updatedGroup?.identifier);
    if (user.groups && groupIndex !== -1 && groupIndex !== undefined) {
      user.groups[groupIndex] = cloneDeep(groupStore.updatedGroup!);
    }
  });
  studyUsersStore.currentUsers = [...studyUsersStore.currentUsers];
};

const openUserPermissionDialog = ref<boolean>(false);
const selectedUser = ref<UserModel>();

const userPermissionEditClick = (data: UserModel) => {
  selectedUser.value = cloneDeep(data);
  openCloseUserPermissionDialog();
};

const openCloseUserPermissionDialog = (userModel?: UserModel) => {
  openUserPermissionDialog.value = !openUserPermissionDialog.value;
  const userIndex = studyUsersStore.currentUsers.findIndex(u => u.identifier === userModel?.identifier);
  if (userIndex !== -1 && userModel) {
    studyUsersStore.currentUsers[userIndex] = cloneDeep(userModel);
  }
};
</script>

<template>
  <Table :columns="studyUsersStore.tableColumns"
    :data-list="studyUsersStore.currentUsers"
    :loading-state="studyUsersStore.isCurrentUsersLoading.isLoading"
    :expandedRows="expandedRows"
    :rows="10"
    @onPerPageRowCountChanged="onPerPageRowCountChanged"
    @onRowClick="handleRowToggle">
    <template #bodyContent="{ data, col }">
      <UserAvatar :avatarUrl="data.avatar"
        :userName="data[col.key]"
        :user-email="data.email"
        :size="44"
        v-if="col.type === TableColumnType.USER"/>

      <div v-if="
          col.key === 'permissionsList' &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        "
        class="permissions-container">
        <span v-for="(permission, index) in getUserCombinedPermissions(data).permissionsList"
          :key="index">
          <Chip :label="permission" />
        </span>
      </div>

      <div v-else-if="
          col.type === TableColumnType.TEXT &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        ">
        {{ data[col.key] }}
      </div>
    </template>
    <template #expandContent="{ data }">
      <UserExpandedNew :user="data"
        @onGroupEdit="groupEditClick"
        @onUserEdit="userPermissionEditClick"/>
    </template>
  </Table>
  <StudyGroupDialog :visible="openGroupDialog"
    v-if="openGroupDialog"
    @onClose="openCloseGroupDialog"/>
  <UserExpanded :visible="openUserPermissionDialog"
    v-if="openUserPermissionDialog && selectedUser"
    :user="selectedUser"
    @onClose="openCloseUserPermissionDialog"/>
</template>

<style scoped lang="scss">
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}

.permissions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}
</style>
